import { WORKSPACE_TEMPLATES } from "./workspaceTemplates";

export type WorkspaceType = "program" | "web" | "sql";

export interface WorkspaceTemplateDef {
    value: string;
    label?: string;
    slots: string[]; // The editors of the template: ["main"], ["html", "css"]...
    language?: string; // The language of a program template
}

// Anything that describes a workspace (the admin's and the learner's types both fit)
export interface WorkspaceLike {
    type?: string;
    template?: string;
    language?: string;
}

// The table is plain JavaScript, so its shape is declared here once
const templatesByType = WORKSPACE_TEMPLATES as unknown as Record<string, WorkspaceTemplateDef[]>;

// Used when a project's template isn't in the table (an old or renamed one)
const FALLBACK_SLOTS: Record<string, string[]> = { program: ["main"], web: ["html"], sql: ["sql"] };

const SLOT_LANGUAGES: Record<string, string> = { html: "html", css: "css", js: "javascript", sql: "sql" };
const SLOT_LABELS: Record<string, string> = { html: "HTML", css: "CSS", js: "JavaScript", sql: "SQL" };

// The templates of a workspace type ("program", "web", "sql")
export const getTemplates = (type: string): WorkspaceTemplateDef[] => templatesByType[type] ?? [];

export const findTemplate = (workspace: WorkspaceLike): WorkspaceTemplateDef | undefined =>
    getTemplates(workspace.type ?? "").find((template) => template.value === workspace.template);

// The editors a project of this workspace has
export const getSlots = (workspace: WorkspaceLike): string[] =>
    findTemplate(workspace)?.slots ?? FALLBACK_SLOTS[workspace.type ?? "program"] ?? ["main"];

// "main" is the program editor: the workspace's setting wins, then the template's own language
export const getSlotLanguage = (workspace: WorkspaceLike, slot: string): string =>
    slot === "main"
        ? workspace.language || findTemplate(workspace)?.language || "python"
        : SLOT_LANGUAGES[slot] ?? slot;

// The name of an editor tab ("HTML", "CSS"...). "main" has none: a single editor is just "Code".
export const getSlotLabel = (slot: string): string | null => SLOT_LABELS[slot] ?? null;